// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import { el, icon } from './dom';
import { GLOSSARY_TERMS } from './glossary';

export function openAbout() {
  if (document.querySelector('.modal-backdrop')) return;
  const close = () => {
    backdrop.remove();
    history.replaceState(null, '', location.pathname + location.search + (location.hash.replace('#about-modal', '') || ''));
  };

  const backdrop = el('div', {
    class: 'modal-backdrop',
    role: 'dialog',
    'aria-modal': 'true',
    onclick: (ev: Event) => { if (ev.target === backdrop) close(); }
  });

  const card = el('div', { class: 'modal-card' });
  card.appendChild(el('div', { class: 'modal-header' }, [
    el('h2', null, 'About Pollution Watch'),
    el('button', { class: 'header-action', 'aria-label': 'Close', onclick: close }, [icon('close', 18)])
  ]));

  card.appendChild(el('div', { class: 'modal-section' }, [
    el('h3', null, 'What is this?'),
    el('p', null, 'Pollution Watch is an unofficial, mobile-friendly view of the Australian Government\'s National Pollutant Inventory (NPI) — a 25-year public record of emissions to air, water and land, plus off-site waste transfers, reported each year by every facility above a regulatory reporting threshold.'),
    el('p', null, 'The data is published by the Department of Climate Change, Energy, the Environment and Water (DCCEEW). It is the same data shown at npi.gov.au, just re-rendered as a fast, searchable, drill-down dashboard so a journalist or a nearby resident can actually use it.')
  ]));

  card.appendChild(el('div', { class: 'modal-section' }, [
    el('h3', null, 'Important caveats'),
    el('ul', { style: 'padding-left: 1.25rem; color: var(--text-secondary); display:flex; flex-direction:column; gap:6px;' }, [
      el('li', null, 'Facilities only report once they exceed a substance-specific reporting threshold. Small operators may legitimately emit but not appear.'),
      el('li', null, 'Most figures are estimated using approved emission factors, not directly measured — accuracy varies by substance and industry.'),
      el('li', null, 'A drop in emissions can reflect a facility closure, reduced production, or a change in measurement methodology, not necessarily an improvement.'),
      el('li', null, 'For compliance or legal use, always cross-check against the official register at npi.gov.au.')
    ])
  ]));

  card.appendChild(el('div', { class: 'modal-section' }, [
    el('h3', null, 'Data refresh'),
    el('p', null, 'The NPI publishes a full annual extract in March each year, covering the previous Australian financial year. This site re-pulls the data monthly via an automated GitHub Actions pipeline.')
  ]));

  card.appendChild(el('div', { class: 'modal-section' }, [
    el('h3', null, 'Glossary'),
    el('dl', { class: 'glossary-list' },
      GLOSSARY_TERMS.flatMap(t => [
        el('dt', null, t.label),
        el('dd', null, t.definition)
      ])
    )
  ]));

  card.appendChild(el('div', { class: 'modal-section' }, [
    el('h3', null, 'Source'),
    el('p', { html: 'Data: <a href="https://data.gov.au/data/dataset/043f58e0-a188-4458-b61c-04e5b540aea4" target="_blank" rel="noopener">National Pollutant Inventory on data.gov.au</a> (CC BY 4.0).' }),
    el('p', { html: 'Population figures: ABS Estimated Resident Population, June 2024.' }),
    el('p', { html: 'Maps: Leaflet + © OpenStreetMap contributors, © CARTO.' })
  ]));

  backdrop.appendChild(card);
  document.body.appendChild(backdrop);
  // Trap escape
  const onKey = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);
}
